<template>
  <div class="view result" :style="{ '--p-color': persona.color, '--p-glow': persona.glowColor }">
    <div class="result__header">
      <p class="result__eyebrow orbitron">ALTER EGO GENERAT</p>
      <h1 class="result__title orbitron">{{ persona.name.toUpperCase() }}</h1>
    </div>

    <div class="result__content">
      <!-- Photo -->
      <div class="result__media-card glass-card">
        <p class="result__media-label orbitron">POZA TA AI</p>
        <div class="result__media-frame">
          <img v-if="session.generatedPhoto" :src="session.generatedPhoto" class="result__img" />
          <div v-else class="result__placeholder">
            <span class="orbitron">FOTO</span>
          </div>
        </div>
      </div>

      <!-- Video -->
      <div class="result__media-card glass-card">
        <p class="result__media-label orbitron">FILMUL TĂU AI</p>
        <div class="result__media-frame">
          <video
            v-if="session.generatedVideo"
            :src="session.generatedVideo"
            class="result__video"
            autoplay
            loop
            muted
            playsinline
          />
          <div v-else class="result__placeholder">
            <span class="orbitron">VIDEO</span>
          </div>
        </div>
      </div>

      <!-- QR code -->
      <div class="result__qr-card glass-card">
        <p class="result__media-label orbitron">SCANEAZĂ PENTRU<br>A DESCĂRCA</p>
        <div class="result__qr-wrap">
          <img v-if="qrDataUrl" :src="qrDataUrl" class="result__qr" />
          <div v-else class="result__qr-spinner">
            <div class="spinner-ring"></div>
          </div>
        </div>
        <p class="result__qr-sub">Descarcă poza și filmul tău</p>
      </div>
    </div>

    <!-- Countdown reset -->
    <div class="result__footer">
      <div class="result__countdown-bar">
        <div class="result__countdown-fill" :style="{ width: countdownPct + '%' }"></div>
      </div>
      <p class="result__countdown-text orbitron">
        SESIUNE NOUĂ ÎN {{ timeLeft }}s
      </p>
    </div>

    <button class="neon-btn result__restart" @click="restart">
      ÎNCEPE DIN NOU
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/store/session'
import { PERSONAS } from '@/data/personas'
import QRCode from 'qrcode'
import { API_BASE_URL } from '../../constants'

const router = useRouter()
const session = useSessionStore()

const persona = computed(() => PERSONAS[session.personaId] || PERSONAS.authentic)
const qrDataUrl = ref(null)

const RESET_SECONDS = 45
const timeLeft = ref(RESET_SECONDS)
const countdownPct = computed(() => (timeLeft.value / RESET_SECONDS) * 100)

let countdownTimer = null

function restart() {
  clearInterval(countdownTimer)
  session.reset()
  router.push('/')
}

onMounted(async () => {
  const downloadUrl = `${API_BASE_URL}/api/result/${session.photoJobId}/${session.videoJobId}`
  try {
    qrDataUrl.value = await QRCode.toDataURL(downloadUrl, {
      width: 250,
      margin: 1,
      color: { dark: '#ffffff', light: '#00000000' },
    })
  } catch {
    // QR generation failed silently
  }

  countdownTimer = setInterval(() => {
    timeLeft.value--
    if (timeLeft.value <= 0) restart()
  }, 1000)
})

onUnmounted(() => clearInterval(countdownTimer))
</script>

<style scoped>
.result {
  padding: 2rem 3rem;
  gap: 1.2rem;
  --p-color: #7c3aed;
  --p-glow: rgba(139, 92, 246, 0.6);
}

.result__header { text-align: center; }

.result__eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.4em;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 0.3rem;
}

.result__title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  letter-spacing: 0.12em;
  color: #fff;
  text-shadow: 0 0 30px var(--p-glow);
}

.result__content {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1.5rem;
  width: 100%;
  align-items: stretch;
}

.result__media-card {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 1.2rem;
  min-height: 0;
}

.result__media-label {
  font-size: 0.7rem;
  letter-spacing: 0.3em;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
}

.result__media-frame {
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,0.06);
}

.result__img,
.result__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.result__placeholder {
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  letter-spacing: 0.2em;
  color: rgba(255,255,255,0.15);
}

/* ─── QR card ─── */
.result__qr-card {
  width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  flex-shrink: 0;
}

.result__qr-wrap {
  width: 220px;
  height: 220px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,0.08);
  padding: 1rem;
}

.result__qr {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 6px;
}

.result__qr-sub {
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.3);
  text-align: center;
  line-height: 1.5;
}

.result__qr-spinner {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-ring {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid rgba(139, 92, 246, 0.2);
  border-top-color: rgba(139, 92, 246, 0.8);
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Footer countdown ─── */
.result__footer {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.result__countdown-bar {
  width: 100%;
  height: 3px;
  background: rgba(255,255,255,0.05);
  border-radius: 3px;
  overflow: hidden;
}

.result__countdown-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--p-color), rgba(96, 165, 250, 0.6));
  transition: width 1s linear;
  box-shadow: 0 0 6px var(--p-glow);
}

.result__countdown-text {
  font-size: 0.7rem;
  letter-spacing: 0.25em;
  color: rgba(255,255,255,0.25);
  text-align: center;
}

.result__restart {
  align-self: center;
  margin-top: 0.4rem;
  padding: 1.1rem 3.5rem;
  font-size: 1.2rem;
  color: #fff;
  background: linear-gradient(135deg, var(--p-color), var(--p-glow));
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 30px var(--p-glow), 0 0 60px var(--p-glow), inset 0 0 20px rgba(255, 255, 255, 0.15);
}

.result__restart:active {
  box-shadow: 0 0 45px var(--p-glow), 0 0 80px var(--p-glow), inset 0 0 25px rgba(255, 255, 255, 0.25);
}
</style>
